import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { t } from 'i18next';

import {
  FORM_TABLE_COLUMN_NAME,
  FORM_TABLE_FIRST_COLUMN_NAME,
} from 'components/_basic/Form/FormTableComponent/constants';

import { getAdditionalColumnProperties } from '../../helpers';

export const FIRST_COLUMN_WIDTH = 342;
export const FIRST_COLUMN_MAXLENGTH = 150;
export const MAX_FIELD_LENGTH = 2;

export const SALES_AND_MARKETING_FORM_NAMES = {
  SALES_AND_MARKETING_CURRENCY: 'salesAndMarketingCurrency',
  SALES_AND_MARKETING_TABLE: 'salesAndMarketingTable',
  SALES_AND_MARKETING_TABLE_TOTAL: 'totalSalesAndMarketingTable',
} as const;

export const useGetItemsColumnDefaultValues = () => {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => [
      'Salaries and commissions for sales staff',
      'Benefits and payroll taxes for sales staff',
      'Depreciation of fixed assets',
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.trainingAndCertificationOfStaff'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.fuelForTransportation'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.travelExpenses'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.officeSuppliesForSalesDepartment'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.advertisingAndPromotions'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.marketingResearch'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.eventsAndTradeShows'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.onlineMarketingSeoSocialMediaEtc'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.customerRelationshipManagementSystemsCrm'),
      t('pdfPages.mappings.financialPlan.salesAndMarketingForm.other'),
    ],
    [t, i18n],
  );
};

export const itemsColumnDefaultValues = [
  'Salaries and commissions for sales staff',
  'Benefits and payroll taxes for sales staff',
  'Depreciation of fixed assets',
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.trainingAndCertificationOfStaff'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.fuelForTransportation'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.travelExpenses'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.officeSuppliesForSalesDepartment'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.advertisingAndPromotions'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.marketingResearch'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.eventsAndTradeShows'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.onlineMarketingSeoSocialMediaEtc'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.customerRelationshipManagementSystemsCrm'),
  t('pdfPages.mappings.financialPlan.salesAndMarketingForm.other'),
];

export const salesAndMarketingColumns = [
  {
    title: 'financialPlan.plannedExpenses.salesAndMarketing.columnTitles.plannedMonthlyExpenses',
    id: 'financialPlan.plannedExpenses.salesAndMarketing.columnTitles.plannedMonthlyExpenses',
  },
  {
    title: 'financialPlan.plannedExpenses.salesAndMarketing.columnTitles.annualExpenseGrowthRate',
    id: 'financialPlan.plannedExpenses.salesAndMarketing.columnTitles.annualExpenseGrowthRate',
  },
];

const additionalProperties: Record<string, string> = getAdditionalColumnProperties(
  FORM_TABLE_COLUMN_NAME,
  MAX_FIELD_LENGTH,
);

export const defaultSalesAndMarketingItem = { [FORM_TABLE_FIRST_COLUMN_NAME]: '', ...additionalProperties };

export const getDefaultSalesAndMarketingRows = (defaultValues: string[]) =>
  defaultValues.map((item) => {
    return { [FORM_TABLE_FIRST_COLUMN_NAME]: item, ...additionalProperties };
  });

export type IRow = Record<string, string | number | undefined>;

export interface ISalesAndMarketingValuesType {
  [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_CURRENCY]: string;
  [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE]: IRow[];
  [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE_TOTAL]: IRow;
}

export const useGetDefaultSalesAndMarketingRows = () => {
  const defaultValues = useGetItemsColumnDefaultValues();

  return useMemo(() => getDefaultSalesAndMarketingRows(defaultValues), [defaultValues]);
};

export const useGetSalesAndMarketingInitialValues = (): ISalesAndMarketingValuesType => {
  const defaultRows = useGetDefaultSalesAndMarketingRows();

  return useMemo(
    () => ({
      [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
      [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE]: defaultRows,
      [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE_TOTAL]: defaultSalesAndMarketingItem,
    }),
    [defaultRows],
  );
};

export const salesAndMarketingInitialValues = {
  [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
  [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE]: getDefaultSalesAndMarketingRows(itemsColumnDefaultValues),
  [SALES_AND_MARKETING_FORM_NAMES.SALES_AND_MARKETING_TABLE_TOTAL]: defaultSalesAndMarketingItem,
};
